import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { serviceUris } from '../../../../environments/services';

export interface DatasetDownloadResponse {
  datasetName: string;
  batchNumber?: number;
  rows: string[];
  allDataProcessed: boolean;
}

export interface DatasetDownloadApiResponse {
  data: DatasetDownloadResponse;
}

@Injectable({
  providedIn: 'root'
})
export class DatasetDownloadService {
  private readonly http = inject(HttpClient);

  private readonly BATCH_LIMIT = environment.production ? 2000 : 1000;
  private readonly MAX_ROWS = 10000;

  downloadDataset(reportType: string, startDate: string, endDate: string): Observable<{ datasetName: string; rows: string[] }> {
    const url = `${environment.backOfficeUrl}${serviceUris.datasetDownload}`;

    const fetchBatch = (offset: number): Observable<DatasetDownloadResponse & { offset: number }> => {
      const params: any = { reportType, startDate, endDate, offset, limit: this.BATCH_LIMIT };
      return this.http.get<DatasetDownloadApiResponse>(url, { params }).pipe(map((res) => ({ ...(res.data ?? (res as any)), offset })));
    };

    return fetchBatch(0).pipe(
      expand((result) => {
        const totalFetched = result.offset + (result.rows?.length ?? 0);
        const stop = result.allDataProcessed || !result.rows?.length || totalFetched >= this.MAX_ROWS;
        if (stop) {
          return EMPTY;
        }
        return fetchBatch(result.offset + this.BATCH_LIMIT);
      }),
      reduce(
        (acc, result) => ({
          datasetName: result.datasetName || acc.datasetName,
          rows: [...acc.rows, ...(result.rows ?? [])]
        }),
        { datasetName: '', rows: [] as string[] }
      )
    );
  }
}
